
import { UseFormReturn } from "react-hook-form";
import { Info } from "lucide-react";
import { CampaignFormValues } from "../CampaignForm";
import { 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormDescription, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

type VoucherDetailsProps = {
  form: UseFormReturn<CampaignFormValues>;
  imagePreview: string | null;
  setImagePreview: (preview: string | null) => void;
};

export function VoucherDetails({ form, imagePreview, setImagePreview }: VoucherDetailsProps) {
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        form.setValue("voucherImage", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium">Voucher Details</h3>
        <p className="text-sm text-muted-foreground">
          Information that will be displayed on the voucher
        </p>
      </div>

      <div className="space-y-4">
        <FormLabel>Voucher Image</FormLabel>
        <div className="flex flex-col gap-4 items-center justify-center border-2 border-dashed border-border rounded-lg p-4">
          {imagePreview ? (
            <div className="relative w-full">
              <img
                src={imagePreview}
                alt="Voucher preview"
                className="max-h-48 object-contain mx-auto rounded-md"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  setImagePreview(null);
                  form.setValue("voucherImage", undefined);
                }}
              >
                Remove Image
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center space-y-2">
                <div className="flex justify-center">
                  <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center">
                    <Info className="w-6 h-6 text-muted-foreground" />
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium">Upload voucher image</p>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG or GIF, max 5MB
                  </p>
                </div>
                <Button type="button" variant="secondary" size="sm" asChild>
                  <label>
                    Choose File
                    <input
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <FormField
        control={form.control}
        name="voucherDetails"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Voucher Details</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Details about what the voucher offers"
                {...field}
              />
            </FormControl>
            <FormDescription>
              This text will be shown on the voucher
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="sponsorName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sponsor Name</FormLabel>
            <FormControl>
              <Input placeholder="Brand or sponsor name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="sponsorMessage"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sponsor Message</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Optional message from the sponsor"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
